import React, { useState, useEffect } from 'react';
import { X, MapPin, User, Phone, Car, Check, Navigation } from 'lucide-react';
import { getPassengers, getDrivers, createRide } from '../../services/api';
import { toast } from 'sonner';
import PlacesAutocompleteInput from './PlacesAutocompleteInput';

const CreateBookingModal = ({ isOpen, onClose }) => {
  const [passengers, setPassengers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    passenger_id: '',
    passenger_name: '',
    passenger_phone: '',
    pickup_address: '',
    dropoff_address: '',
    pickup_lat: 41.3275,
    pickup_lng: 19.8187,
    dropoff_lat: 41.3300,
    dropoff_lng: 19.8200,
    driver_id: '',
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [passengersData, driversData] = await Promise.all([
        getPassengers(),
        getDrivers()
      ]);
      setPassengers(passengersData);
      setDrivers(driversData.filter(d => d.status === 'online'));
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const handlePassengerSelect = (passengerId) => {
    const passenger = passengers.find(p => p.id === passengerId);
    if (passenger) {
      setFormData(prev => ({
        ...prev,
        passenger_id: passenger.id,
        passenger_name: passenger.name,
        passenger_phone: passenger.phone || ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.passenger_name || !formData.pickup_address || !formData.dropoff_address) {
      toast.error('Plotësoni të gjitha fushat e detyrueshme');
      return;
    }

    setSubmitting(true);
    try {
      const rideData = {
        passenger_id: formData.passenger_id || null,
        passenger_name: formData.passenger_name,
        passenger_phone: formData.passenger_phone,
        pickup: {
          address: formData.pickup_address,
          lat: formData.pickup_lat,
          lng: formData.pickup_lng
        },
        dropoff: {
          address: formData.dropoff_address,
          lat: formData.dropoff_lat,
          lng: formData.dropoff_lng
        },
        notes: formData.notes
      };

      await createRide(rideData);
      toast.success('Porosia u krijua me sukses!');
      
      onClose();
      // Reset form
      setFormData({
        passenger_id: '',
        passenger_name: '',
        passenger_phone: '',
        pickup_address: '',
        dropoff_address: '',
        pickup_lat: 41.3275,
        pickup_lng: 19.8187,
        dropoff_lat: 41.3300,
        dropoff_lng: 19.8200,
        driver_id: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error creating ride:', error);
      toast.error('Gabim gjatë krijimit të porosisë');
    }
    setSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden"
        style={{ fontFamily: "'Inter Tight', sans-serif" }}
      >
        {/* Header */}
        <div 
          className="px-6 py-4 border-b flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg, #dc2313, #b91c1c)' }}
        >
          <h2 className="text-xl font-bold text-white">Krijo Porosi të Re</h2>
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Passenger Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-2" />
              Pasagjeri
            </label>
            <select
              value={formData.passenger_id}
              onChange={(e) => handlePassengerSelect(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all"
            >
              <option value="">-- Zgjidhni ose shkruani manual --</option>
              {passengers.map(p => (
                <option key={p.id} value={p.id}>{p.name} - {p.phone}</option>
              ))}
            </select>
          </div>

          {/* Manual passenger input */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Emri</label>
              <input
                type="text"
                value={formData.passenger_name}
                onChange={(e) => setFormData(prev => ({ ...prev, passenger_name: e.target.value }))}
                placeholder="Emri i pasagjerit"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Telefoni</label>
              <input
                type="tel"
                value={formData.passenger_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, passenger_phone: e.target.value }))}
                placeholder="+355..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              />
            </div>
          </div>

          {/* Pickup Address with Autocomplete */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <MapPin className="w-4 h-4 inline mr-2 text-green-500" />
              Adresa e Marrjes
            </label>
            <PlacesAutocompleteInput
              value={formData.pickup_address}
              onChange={(place) => setFormData(prev => ({
                ...prev,
                pickup_address: place.address,
                pickup_lat: place.lat || prev.pickup_lat,
                pickup_lng: place.lng || prev.pickup_lng
              }))}
              placeholder="Ku do ta marrë pasagjerin?"
              type="pickup"
              country="al"
            />
          </div>

          {/* Dropoff Address with Autocomplete */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Navigation className="w-4 h-4 inline mr-2 text-red-500" />
              Adresa e Destinacionit
            </label>
            <PlacesAutocompleteInput
              value={formData.dropoff_address}
              onChange={(place) => setFormData(prev => ({
                ...prev,
                dropoff_address: place.address,
                dropoff_lat: place.lat || prev.dropoff_lat,
                dropoff_lng: place.lng || prev.dropoff_lng
              }))}
              placeholder="Ku do të shkojë?"
              type="dropoff"
              country="al"
            />
          </div>

          {/* Driver Selection (Optional) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Car className="w-4 h-4 inline mr-2" />
              Cakto Shoferin (Opsional)
            </label>
            <select
              value={formData.driver_id}
              onChange={(e) => setFormData(prev => ({ ...prev, driver_id: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
            >
              <option value="">-- Pa shofer (do caktohet më vonë) --</option>
              {drivers.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name} - {d.vehicle?.plate || 'Pa targa'}
                </option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Shënime</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Shënime shtesë për shoferin..."
              rows={2}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 rounded-xl font-bold text-white transition-all hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg, #fabc0c, #e0a80a)' }}
          >
            {submitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Duke krijuar...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Krijo Porosinë
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateBookingModal;
